import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabaseClient';
import type {
  CampaignPdvSupervisorRow, CampaignRow, CampaignScheduleRow, CampaignTypeRow, FormFieldRow,
  ProfileRow,
} from '../lib/db-types';

export interface CampaignInput {
  /** Nombre propio de la campaña ("Troperos Pringles"). Opcional: hay campañas que solo se
   *  conocen por cliente y marca. */
  name?: string | null;
  client: string;
  brand: string;
  /** Código de `campaign_types` (FK desde 0013): el conjunto lo define la maestra, no el código. */
  type: string;
  description: string | null;
  quota: number | null;
  start_date: string;
  end_date: string;
  comercial_id: string | null;
  coordinator_id: string | null;
  status: 'borrador' | 'activa' | 'cerrada';
  /** Opcional a propósito: si la clave no está, el insert/update no toca la columna. */
  analytic_account?: string | null;
}

/** Tipos de campaña vigentes, en el orden en que se muestran. */
export async function listCampaignTypes(): Promise<CampaignTypeRow[]> {
  const { data, error } = await supabase
    .from('campaign_types')
    .select('*')
    .eq('active', true)
    .order('sort')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as CampaignTypeRow[];
}

/**
 * Campañas de esa cuenta analítica cuyo periodo se cruza con [from, to].
 * La usa el importador para avisar antes de crear por segunda vez la programación de un mes
 * que ya se cargó: la cuenta analítica se repite todos los meses, así que no puede ser única
 * en la tabla y el duplicado solo se puede detectar por periodo.
 */
export async function findCampaignsByAnalyticAccount(
  account: string, from: string, to: string,
): Promise<CampaignRow[]> {
  const { data, error } = await supabase
    .from('campaigns')
    // A propósito SIN filtrar las archivadas: esto detecta duplicados al importar. Si una
    // campaña archivada no contara, el importador dejaría cargar otra igual y terminarían dos
    // en la base con la misma cuenta analítica y el mismo periodo.
    .select('*')
    .eq('analytic_account', account)
    .lte('start_date', to)
    .gte('end_date', from)
    .order('start_date');
  if (error) throw new Error(error.message);
  return (data ?? []) as CampaignRow[];
}

/**
 * Campañas visibles. Las **archivadas quedan fuera** salvo que se pidan explícitamente.
 *
 * Archivar reemplaza al borrado (migración 0027): borrar una campaña dejaba fotos huérfanas en
 * el Storage —que Supabase no permite borrar por SQL— y destruía el rastro de que existió. Con
 * canjes sería peor: cada registro tiene dos fotos y evidencia de producto entregado.
 *
 * Este es el listado que consumen TODAS las pantallas del panel (Asignaciones, Campañas,
 * Validación, Cierres, el importador…), así que filtrar acá las esconde de todas de una vez.
 */
export async function listCampaigns(
  opciones: { incluirArchivadas?: boolean; soloVigentes?: boolean } = {},
): Promise<CampaignRow[]> {
  let q = supabase.from('campaigns').select('*');
  if (!opciones.incluirArchivadas) q = q.is('archived_at', null);
  if (opciones.soloVigentes) {
    // Pedido del usuario (1-sep): los filtros del panel muestran solo lo vivo. Ver
    // `listCampaignOptions` para el porqué de cada condición; acá se aplican sobre la ficha
    // completa y contra el mes en curso MÁS EL ANTERIOR (ver `rangoDeVigentes`).
    const { desde, hasta } = rangoDeVigentes(formatInTimeZone(new Date(), 'America/Lima', 'yyyy-MM-dd'));
    q = q.eq('es_banco_pruebas', false).lte('start_date', hasta).gte('end_date', desde);
  }
  const { data, error } = await q.order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as CampaignRow[];
}

/** Lo mínimo que un desplegable de campañas necesita mostrar. */
export interface CampaignOption {
  id: string;
  client: string;
  brand: string;
}

/**
 * Las campañas que un filtro del panel debe OFRECER: las vigentes en el periodo que se mira.
 *
 * La regla no se inventa acá: es la misma que `fetchCampaniasDelMes` (planning) ya aplicaba a la
 * matriz de planificación, y ahora vive en un solo lugar para que todas las pantallas la compartan.
 * Son tres condiciones:
 *
 * 1. **No archivada.** Archivar reemplaza al borrado (0027) y su sentido es desaparecer del panel.
 * 2. **No banco de pruebas.** Las campañas DEMO existen para ensayar, no para operar.
 * 3. **Su periodo cruza el rango pedido** (`start_date <= hasta && end_date >= desde`). Cruce, no
 *    contención: una campaña de agosto a junio tiene que salir cuando se mira setiembre.
 *
 * **Por qué recibe el rango y no usa «hoy» a secas.** El cierre de agosto se hace en setiembre, y
 * las campañas que terminaron el 31 de agosto ya no están vigentes hoy. Si el filtro mirara el día
 * de hoy, la pantalla de Cierres escondería justo la campaña que hay que cerrar. Cada pantalla pasa
 * el periodo que de verdad está mirando.
 */
export async function listCampaignOptions(
  desde: string, hasta: string,
): Promise<CampaignOption[]> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('id, client, brand')
    .is('archived_at', null)
    .eq('es_banco_pruebas', false)
    .lte('start_date', hasta)
    .gte('end_date', desde)
    .order('client')
    .order('brand');
  if (error) throw new Error(error.message);
  return (data ?? []) as CampaignOption[];
}

/** El mes de una fecha `'YYYY-MM-DD'` como rango, para pasárselo a `listCampaignOptions`. */
export function rangoDelMesDe(fecha: string): { desde: string; hasta: string } {
  const [y, m] = fecha.split('-').map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { desde: `${y}-${mm}-01`, hasta: `${y}-${mm}-${String(ultimo).padStart(2, '0')}` };
}

/**
 * Lo que Asignaciones y Campañas consideran «vigente»: del primer día del mes ANTERIOR al último del mes en curso.
 *
 * Mostraban solo el mes en curso, y el 1-nov desapareció «Bimbo - Vital Cero Cero Octubre» (terminó el 31) justo
 * cuando había que regularizar las asignaciones, las fotos y los cierres de las que faltaron. Lo de un mes se
 * regulariza en los primeros días del siguiente, así que el mes que terminó sigue a la vista todo el mes siguiente.
 * Recibe la fecha de Lima: con la de UTC, desde las 19:00 del último día del mes ya se miraba el mes siguiente.
 */
export function rangoDeVigentes(fecha: string): { desde: string; hasta: string } {
  const [y, m] = fecha.split('-').map(Number);
  const anterior = m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
  return { desde: rangoDelMesDe(anterior).desde, hasta: rangoDelMesDe(fecha).hasta };
}

/**
 * Archiva (o restaura) una campaña. Devuelve cuántas asignaciones VIVAS tenía, para poder
 * avisarlo: archivar no cancela turnos, así que si tiene gente asignada esa gente va a seguir
 * viendo su turno en el celular aunque la campaña desaparezca del panel.
 */
export async function archivarCampania(id: string, archivar: boolean): Promise<number> {
  const { data, error } = await supabase.rpc('archivar_campania', {
    p_campaign: id,
    p_archivar: archivar,
  });
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

export async function getCampaign(id: string): Promise<CampaignRow> {
  const { data, error } = await supabase.from('campaigns').select('*').eq('id', id).single();
  if (error) throw new Error(error.message);
  return data as CampaignRow;
}

export async function createCampaign(data: CampaignInput): Promise<CampaignRow> {
  const { data: created, error } = await supabase
    .from('campaigns')
    .insert(data)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return created as CampaignRow;
}

export async function updateCampaign(id: string, data: Partial<CampaignInput>): Promise<CampaignRow> {
  const { data: updated, error } = await supabase
    .from('campaigns')
    .update(data)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return updated as CampaignRow;
}

export interface FilaHorarioImportada {
  pdvId: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  headcount: number;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
  /** Las fechas MARCADAS en la grilla. De acá salen las jornadas, una por fecha y recurso. */
  fechas: string[];
}

/**
 * Carga horarios y jornadas de una campaña desde la plantilla, en UNA transacción.
 *
 * Va por RPC y no por `upsertSchedule` + `insert_positions` desde el cliente: son dos tablas y
 * decenas de filas. Si la carga se cortara a la mitad quedaría una campaña con horarios sin
 * jornadas —o peor, con la mitad de las jornadas—, y ese descuadre no se nota hasta el cierre de
 * pago. Es la misma razón de `crearDinamica` y `crearVentanas`.
 */
export async function importarHorarios(
  campaignId: string, filas: FilaHorarioImportada[],
): Promise<{ horarios: number; jornadas: number }> {
  const { data, error } = await supabase.rpc('importar_horarios_campania', {
    p_campaign: campaignId,
    p_filas: filas.map((f) => ({
      pdv_id: f.pdvId,
      weekdays: f.weekdays,
      start_time: f.startTime,
      end_time: f.endTime,
      headcount: f.headcount,
      rate_amount: String(f.rateAmount),
      rate_unit: f.rateUnit,
      fechas: f.fechas,
    })),
  });
  if (error) throw new Error(error.message);
  return data as { horarios: number; jornadas: number };
}

export type ScheduleWithPdv = CampaignScheduleRow & { pdv: { code: string; name: string } };

export async function listSchedules(campaignId: string): Promise<ScheduleWithPdv[]> {
  const { data, error } = await supabase
    .from('campaign_schedules')
    .select('*, pdv:pdv_id(code, name)')
    .eq('campaign_id', campaignId)
    .order('created_at');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ScheduleWithPdv[];
}

export interface ScheduleUpsert {
  id?: string;
  campaign_id: string;
  pdv_id: string;
  weekdays: number[];
  extra_dates: string[];
  /** Vigencia (0304). `null` = toda la campaña. Obligatoria para que el typecheck marque a cada creador.
   *  `undefined` = no tocarla (solo al editar): así una pestaña vieja no deshace una baja hecha en otra. */
  vigente_desde: string | null | undefined;
  vigente_hasta: string | null | undefined;
  start_time: string;
  end_time: string;
  headcount: number;
  rate_amount: number;
  rate_unit: 'jornada' | 'hora';
}

export async function upsertSchedule(row: ScheduleUpsert): Promise<CampaignScheduleRow> {
  const { id, ...fields } = row;
  if (id) {
    const { data, error } = await supabase
      .from('campaign_schedules')
      .update(fields)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as CampaignScheduleRow;
  }
  const { data, error } = await supabase
    .from('campaign_schedules')
    .insert(fields)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as CampaignScheduleRow;
}

export async function deleteSchedule(id: string): Promise<void> {
  const { error } = await supabase.from('campaign_schedules').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface SchedulePositionsCount {
  /** Posiciones generadas a partir del horario (se borran en cascada con él). */
  total: number;
  /** De esas, las que ya NO están `abierta`: tienen asignaciones y bloquean el borrado. */
  assigned: number;
}

/** Se consulta ANTES de eliminar un horario, para confirmar el impacto con números reales. */
export async function countSchedulePositions(scheduleId: string): Promise<SchedulePositionsCount> {
  const { count: total, error } = await supabase
    .from('positions')
    .select('id', { count: 'exact', head: true })
    .eq('schedule_id', scheduleId);
  if (error) throw new Error(error.message);
  const { count: assigned, error: assignedError } = await supabase
    .from('positions')
    .select('id', { count: 'exact', head: true })
    .eq('schedule_id', scheduleId)
    .neq('status', 'abierta');
  if (assignedError) throw new Error(assignedError.message);
  return { total: total ?? 0, assigned: assigned ?? 0 };
}

export type CoordinatorOption = Pick<ProfileRow, 'id' | 'first_names' | 'last_names'>;

export async function listCoordinators(): Promise<CoordinatorOption[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_names, last_names')
    .contains('roles', ['coordinador'])
    .eq('status', 'activo')
    .order('first_names');
  if (error) throw new Error(error.message);
  return (data ?? []) as CoordinatorOption[];
}

export type SupervisorOption = Pick<ProfileRow, 'id' | 'first_names' | 'last_names'>;

/**
 * Supervisores activos, para el desplegable de asignación. La RLS de `profiles`
 * (`profiles_select_supervisores_asignables`) es la que permite verlos ANTES de asignarles
 * nada — sin eso, listaría vacío para quien todavía no coordina a ninguno.
 */
export async function listSupervisors(): Promise<SupervisorOption[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_names, last_names')
    .contains('roles', ['supervisor'])
    .eq('status', 'activo')
    .order('first_names');
  if (error) throw new Error(error.message);
  return (data ?? []) as SupervisorOption[];
}

export interface CampaignStore {
  id: string;
  code: string;
  name: string;
  chain: string | null;
}

/**
 * Las tiendas EN ESTA campaña: las que tienen horario cargado (`campaign_schedules`), sin
 * repetir. Mismo origen que ya usa la pestaña Posiciones — una tienda sin horario no tiene nada
 * que un supervisor pueda cubrir en esta campaña. Una tienda puede tener más de un horario, así
 * que se deduplica por id antes de devolver.
 */
export async function listCampaignStores(campaignId: string): Promise<CampaignStore[]> {
  const { data, error } = await supabase
    .from('campaign_schedules')
    .select('pdv:pdv_id(id, code, name, chain)')
    .eq('campaign_id', campaignId);
  if (error) throw new Error(error.message);
  const vistos = new Map<string, CampaignStore>();
  for (const row of (data ?? []) as unknown as { pdv: CampaignStore }[]) {
    vistos.set(row.pdv.id, row.pdv);
  }
  return [...vistos.values()].sort((a, b) =>
    (a.chain ?? '').localeCompare(b.chain ?? '') || a.name.localeCompare(b.name));
}

export type SupervisorAssignment = CampaignPdvSupervisorRow & {
  pdv: CampaignStore;
  profile: Pick<ProfileRow, 'first_names' | 'last_names'>;
};

export async function listSupervisorAssignments(campaignId: string): Promise<SupervisorAssignment[]> {
  const { data, error } = await supabase
    .from('campaign_pdv_supervisors')
    .select('*, pdv:pdv_id(id, code, name, chain), profile:profile_id(first_names, last_names)')
    .eq('campaign_id', campaignId);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SupervisorAssignment[];
}

/**
 * Asigna un supervisor a una o varias tiendas de una sola vez — sirve tanto para una tienda
 * como para "toda una cadena", que es la misma llamada con más ids. `onConflict` hace que
 * asignar de nuevo sobre una tienda REEMPLACE al supervisor anterior ahí, sin acumular filas.
 */
export async function assignSupervisors(
  campaignId: string, pdvIds: string[], profileId: string,
): Promise<void> {
  if (pdvIds.length === 0) return;
  const { error } = await supabase
    .from('campaign_pdv_supervisors')
    .upsert(
      pdvIds.map((pdvId) => ({ campaign_id: campaignId, pdv_id: pdvId, profile_id: profileId })),
      { onConflict: 'campaign_id,pdv_id' },
    );
  if (error) throw new Error(error.message);
}

/** Quita al supervisor de una tienda (la deja "sin supervisor", no la reemplaza por otro). */
export async function unassignSupervisor(assignmentId: string): Promise<void> {
  const { error } = await supabase
    .from('campaign_pdv_supervisors')
    .delete()
    .eq('id', assignmentId);
  if (error) throw new Error(error.message);
}

export interface FormFieldUpsert {
  id?: string;
  campaign_id: string;
  label: string;
  kind: 'texto' | 'numero' | 'seleccion' | 'foto';
  options: string[] | null;
  required: boolean;
  moment: 'ingreso' | 'salida' | 'ambos';
  sort: number;
  /** `null` = el campo se pide siempre; con dinámica, solo donde esa dinámica está vigente. */
  dynamic_id: string | null;
}

export async function listFormFields(campaignId: string): Promise<FormFieldRow[]> {
  const { data, error } = await supabase
    .from('form_fields')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('sort')
    .order('label');
  if (error) throw new Error(error.message);
  return (data ?? []) as FormFieldRow[];
}

/**
 * Los campos que aplican a UN turno: campaña, tienda y fecha.
 *
 * Se diferencia de `listFormFields` en que resuelve las dinámicas vigentes (0029): un campo
 * atado a una dinámica —"¿cuántos raspa y gana entregaste?"— solo se pide donde y cuando esa
 * promoción corre. Preguntárselo a las 14 tiendas que no la tienen convierte un campo
 * obligatorio en un trámite que se rellena con cualquier número para poder marcar salida, y ahí
 * el dato deja de servir para algo.
 *
 * Si la RPC no está disponible, cae a `listFormFields`: se pedirán de más, pero **nunca de
 * menos**, y sobre todo nadie se queda sin poder marcar por una lectura que falló.
 */
export async function listFormFieldsDelTurno(
  campaignId: string, pdvId: string, fecha: string,
): Promise<FormFieldRow[]> {
  const { data, error } = await supabase.rpc('campos_del_turno', {
    p_campaign: campaignId, p_pdv: pdvId, p_fecha: fecha,
  });
  if (error) return listFormFields(campaignId);
  return (data ?? []) as FormFieldRow[];
}

export async function upsertFormField(row: FormFieldUpsert): Promise<FormFieldRow> {
  const { id, ...fields } = row;
  if (id) {
    const { data, error } = await supabase
      .from('form_fields')
      .update(fields)
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as FormFieldRow;
  }
  const { data, error } = await supabase
    .from('form_fields')
    .insert(fields)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as FormFieldRow;
}

export async function deleteFormField(id: string): Promise<void> {
  const { error } = await supabase.from('form_fields').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Las campañas de las que la persona es el coordinador DESIGNADO (`campaigns.coordinator_id`).
 *
 * Es lo que decide si una bandeja le ofrece corregir y anular —canjes desde la 0044, levantamiento
 * desde la 0059—, y por qué no alcanza el rol: `coordinador` lo comparten el coordinador y los
 * supervisores de la campaña, y solo el designado puede accionar. Sin esta lectura la pantalla
 * mostraría botones que el servidor rechaza —"un botón que el servidor va a rechazar es una trampa"
 * (BITACORA §2)—.
 *
 * Vive acá y no en `api/canjes.ts` —donde nació— porque no es un dato de canjes sino de campañas, y
 * las dos bandejas necesitan exactamente la misma respuesta: dos copias de esta lectura se
 * desincronizan sin que nada falle, y la que quede vieja abre o cierra botones de más.
 *
 * No es seguridad: la garantía la dan `puede_editar_canje` y `puede_editar_visita` en el servidor.
 * Esto es encuadre.
 */
export async function campaniasQueCoordino(): Promise<Set<string>> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion?.user?.id;
  if (!uid) return new Set();
  const { data, error } = await supabase
    .from('campaigns')
    .select('id')
    .eq('coordinator_id', uid);
  // Si falla, se devuelve vacío: la bandeja queda de solo lectura. Preferible a ofrecer acciones
  // que van a rebotar sin explicación.
  if (error) return new Set();
  return new Set(((data ?? []) as { id: string }[]).map((c) => c.id));
}
